import React, { useState } from 'react';
import { GradingReport, FileLinks } from '../types';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, CartesianGrid } from 'recharts';
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

interface ResultsViewProps {
  reports: GradingReport[]; 
  onReset: () => void;
  fileLinks?: FileLinks;
}

const ResultsView: React.FC<ResultsViewProps> = ({ reports, onReset, fileLinks }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showLogic, setShowLogic] = useState(false);

  // Safely get current report
  const report = reports[currentIndex];

  if (!report) return <div>No report data available.</div>;

  // Calculate Rubric Level Distribution
  const rubricCounts = {
    'Exemplary': 0,
    'Competent': 0,
    'Developing': 0,
    'Needs Improvement': 0
  };

  report.questionAnalysis.forEach(q => {
    // Normalizing case just in case
    const level = q.rubricLevel || ""; 
    if (level.includes("Exemplary")) rubricCounts['Exemplary']++;
    else if (level.includes("Competent")) rubricCounts['Competent']++;
    else if (level.includes("Developing")) rubricCounts['Developing']++;
    else rubricCounts['Needs Improvement']++;
  });

  const chartData = [
    { name: 'Exemplary', value: rubricCounts['Exemplary'], color: '#22c55e' }, // Green
    { name: 'Competent', value: rubricCounts['Competent'], color: '#3b82f6' }, // Blue
    { name: 'Developing', value: rubricCounts['Developing'], color: '#f59e0b' }, // Amber/Orange
    { name: 'Needs Improvement', value: rubricCounts['Needs Improvement'], color: '#ef4444' } // Red
  ];

  const handleDownloadLogicLog = () => {
    let content = `EVALUATION LOGIC LOG (CROSS-VERIFICATION)\n`;
    content += `Student: ${report.studentName}\n`;
    content += `Subject: ${report.subject}\n`;
    content += `Grade Level: ${report.gradeLevel}\n`;
    content += `Files Used: ${report.pagesUsed?.map(p => `File ${p}`).join(', ') || 'N/A'}\n`;
    content += `Segregation Logic: ${report.segregationReasoning || 'N/A'}\n`;
    content += `Total Score: ${report.totalScore}/${report.maxTotalScore}\n`;
    content += `Date Generated: ${new Date().toLocaleString()}\n`;
    content += `=================================================================\n\n`;

    report.questionAnalysis.forEach(q => {
      content += `[Q${q.questionNumber}] ${q.questionText}\n`;
      content += `-----------------------------------------------------------------\n`;
      content += `Student Answer: ${q.studentAnswerSummary}\n`;
      content += `Points Awarded: ${q.awardedPoints} / ${q.maxPoints}\n`;
      content += `Rubric Level: ${q.rubricLevel}\n`;
      content += `\nINTERNAL EVALUATION LOGIC:\n${q.evaluationLogic || "No logic recorded."}\n`;
      content += `\n=================================================================\n\n`;
    });

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${report.studentName.replace(/\s+/g, '_')}_Logic_Log.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const generatePDF = (targetReport: GradingReport) => {
    const doc = new jsPDF();

    // Header
    doc.setFontSize(22);
    doc.setTextColor(14, 165, 233); // Primary color
    doc.text("Grading Report", 14, 20);
    
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    doc.text(`Student: ${targetReport.studentName}`, 14, 30);
    doc.text(`Subject: ${targetReport.subject} | Grade: ${targetReport.gradeLevel}`, 14, 36);
    doc.text(`Score: ${targetReport.totalScore}/${targetReport.maxTotalScore} (${targetReport.percentage}%)`, 14, 42);

    // Separator
    doc.setDrawColor(200, 200, 200);
    doc.line(14, 48, 196, 48);

    // Feedback
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text("Overall Feedback", 14, 58);
    doc.setFontSize(10);
    doc.setTextColor(80, 80, 80);
    
    // Wrap feedback text
    const splitFeedback = doc.splitTextToSize(targetReport.overallFeedback, 180);
    doc.text(splitFeedback, 14, 66);
    
    let yPos = 66 + (splitFeedback.length * 5) + 10;

    // Strengths & Improvements Headers
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0); // Black headers
    doc.text("Strengths", 14, yPos);
    doc.text("Areas for Improvement", 110, yPos);
    yPos += 8;

    doc.setFontSize(10);
    doc.setTextColor(50, 50, 50); // Dark Grey text

    // Render Columns with wrapping
    const colWidth = 85;
    let leftY = yPos;
    let rightY = yPos;

    // Left Column: Strengths
    targetReport.strengths.forEach(strength => {
      const bullet = `• ${strength}`;
      const splitText = doc.splitTextToSize(bullet, colWidth);
      doc.text(splitText, 14, leftY);
      leftY += (splitText.length * 5) + 2; // Line height + padding
    });

    // Right Column: Areas for Improvement
    targetReport.areasForImprovement.forEach(area => {
      const bullet = `• ${area}`;
      const splitText = doc.splitTextToSize(bullet, colWidth);
      doc.text(splitText, 110, rightY);
      rightY += (splitText.length * 5) + 2;
    });
    
    // Set next Y position to the maximum of the two columns + spacing
    yPos = Math.max(leftY, rightY) + 10;

    // Detailed Analysis Table
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text("Detailed Analysis", 14, yPos);
    yPos += 6;

    const tableData = targetReport.questionAnalysis.map(q => {
        const errors = q.spellingErrors && q.spellingErrors.length > 0 
          ? `\n(Spelling: ${q.spellingErrors.join(', ')})` 
          : '';
        
        let evaluationText = q.critique;
        evaluationText += `\n\n[LOGIC]: ${q.evaluationLogic || 'N/A'}`;

        return [
            q.questionNumber,
            `${q.awardedPoints}/${q.maxPoints}`,
            q.rubricLevel || '-',
            `Q: ${q.questionText}\n\nA: ${q.studentAnswerSummary}${errors}`,
            evaluationText
        ];
    });

    autoTable(doc, {
        startY: yPos,
        head: [['Q#', 'Pts', 'Level', 'Q & Student Answer', 'Evaluation & Logic']],
        body: tableData,
        theme: 'grid',
        headStyles: { fillColor: [14, 165, 233] }, // Primary color
        styles: { fontSize: 8, cellPadding: 3, valign: 'top' },
        columnStyles: {
            0: { cellWidth: 10 },
            1: { cellWidth: 15 },
            2: { cellWidth: 25 }, // Slightly wider for rubrics like "Needs Improvement"
            3: { cellWidth: 70 },
            4: { cellWidth: 'auto' }
        },
    });

    doc.save(`${targetReport.studentName.replace(/\s+/g, '_')}_Report.pdf`);
  };

  const handleDownloadCurrentPDF = () => generatePDF(report);
  
  const handleDownloadAllPDFs = () => {
    reports.forEach(r => generatePDF(r));
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8 animate-fade-in pb-12">
      
      {/* Student Navigation Bar */}
      <div className="sticky top-20 z-40 bg-white/95 backdrop-blur shadow-md rounded-xl border border-primary-200 p-2 flex items-center justify-between">
         <div className="flex items-center gap-4">
            <button 
              onClick={() => setCurrentIndex(prev => Math.max(0, prev - 1))}
              disabled={currentIndex === 0}
              className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <svg className="w-6 h-6 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
            </button>
            <div className="text-center">
              <div className="text-xs font-bold text-primary-600 uppercase tracking-wide">Student {currentIndex + 1} of {reports.length}</div>
              <div className="font-bold text-gray-900 text-lg leading-tight">{report.studentName}</div>
            </div>
            <button 
              onClick={() => setCurrentIndex(prev => Math.min(reports.length - 1, prev + 1))}
              disabled={currentIndex === reports.length - 1}
              className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <svg className="w-6 h-6 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
            </button>
         </div>
         
         <div className="flex gap-2">
            <button 
              onClick={handleDownloadAllPDFs}
              className="hidden sm:flex text-xs bg-gray-100 hover:bg-gray-200 text-gray-800 px-3 py-2 rounded-lg font-medium transition-colors items-center gap-1"
            >
               <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
               Download All PDFs
            </button>
         </div>
      </div>

      {/* Header Card */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-slate-900 p-6 text-white flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">{report.studentName}</h1>
            <p className="text-sky-100">{report.subject} • Grade {report.gradeLevel}</p>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold">{report.percentage}%</div>
            <div className="text-sm text-sky-100">{report.totalScore} / {report.maxTotalScore} Points</div>
          </div>
        </div>
        
        <div className="p-6">
          <div className="flex flex-col sm:flex-row justify-between items-start mb-4 gap-2">
             <h3 className="text-lg font-semibold text-gray-800">Overall Feedback</h3>
             {report.pagesUsed && report.pagesUsed.length > 0 && (
               <div className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded border border-gray-200">
                 Source: {report.pagesUsed.map(p => `File ${p}`).join(', ')}
               </div>
             )}
          </div>
          
          <p className="text-gray-600 leading-relaxed bg-gray-50 p-4 rounded-lg border border-gray-100">
            {report.overallFeedback}
          </p>
          
          {report.segregationReasoning && (
             <div className="mt-3 text-xs text-gray-400 italic border-l-2 border-gray-200 pl-2">
               AI Grouping Logic: "{report.segregationReasoning}"
             </div>
          )}

          <div className="grid md:grid-cols-2 gap-6 mt-6">
            <div>
              <h4 className="font-semibold text-green-700 flex items-center gap-2 mb-2">
                <span className="bg-green-100 p-1 rounded">💪</span> Strengths
              </h4>
              <ul className="list-disc list-inside space-y-1 text-gray-600 text-sm">
                {report.strengths.map((s, i) => <li key={i}>{s}</li>)}
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-orange-700 flex items-center gap-2 mb-2">
                <span className="bg-orange-100 p-1 rounded">🎯</span> Areas for Improvement
              </h4>
              <ul className="list-disc list-inside space-y-1 text-gray-600 text-sm">
                {report.areasForImprovement.map((s, i) => <li key={i}>{s}</li>)}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Rubric Level Distribution Chart (Horizontal Bar) */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-800">Rubric Level Distribution</h3>
          <span className="text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded border border-gray-100">Exemplary - Needs Improvement</span>
        </div>
        
        <div className="h-64 w-full">
           <ResponsiveContainer width="100%" height="100%">
              <BarChart
                layout="vertical"
                data={chartData}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" allowDecimals={false} />
                <YAxis 
                   dataKey="name" 
                   type="category" 
                   width={150} 
                   tick={{fontSize: 12, fill: '#4b5563', fontWeight: 500}} 
                />
                <Tooltip 
                  cursor={{fill: '#f9fafb'}}
                  contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}
                />
                <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={32}>
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
           </ResponsiveContainer>
        </div>
        <div className="mt-2 text-center text-xs text-gray-400">
          Showing count of questions falling into each rubric category.
        </div>
      </div>

      {/* Detailed Analysis */}
      <div className="space-y-4">
        <div className="flex justify-between items-center px-1">
          <h3 className="text-xl font-bold text-gray-800">Detailed Question Analysis</h3>
          <label className="flex items-center cursor-pointer">
            <div className="relative">
              <input type="checkbox" className="sr-only" checked={showLogic} onChange={() => setShowLogic(!showLogic)} />
              <div className={`block w-10 h-6 rounded-full transition-colors ${showLogic ? 'bg-primary-600' : 'bg-gray-300'}`}></div>
              <div className={`dot absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${showLogic ? 'transform translate-x-4' : ''}`}></div>
            </div>
            <div className="ml-3 text-sm font-medium text-gray-700">
              Show Evaluation Logic
            </div>
          </label>
        </div>

        {report.questionAnalysis.map((q, idx) => (
          <div key={idx} className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 transition hover:shadow-md">
            
            {/* Header with Points and Rubric Level */}
            <div className="flex flex-wrap justify-between items-start mb-4 gap-2">
              <div className="flex items-center gap-3">
                <span className="bg-gray-100 text-gray-700 font-bold px-3 py-1 rounded-md">Q{q.questionNumber}</span>
                <span className={`text-sm font-semibold px-2 py-0.5 rounded ${q.awardedPoints === q.maxPoints ? 'bg-green-100 text-green-700' : 'bg-blue-50 text-blue-700'}`}>
                  {q.awardedPoints} / {q.maxPoints} pts
                </span>
              </div>
              <span className={`text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide
                ${q.rubricLevel?.includes('Exemplary') ? 'bg-green-100 text-green-700' : 
                  q.rubricLevel?.includes('Competent') ? 'bg-blue-100 text-blue-700' :
                  q.rubricLevel?.includes('Developing') ? 'bg-amber-100 text-amber-700' :
                  'bg-red-100 text-red-700'
                }`}>
                {q.rubricLevel || 'Evaluated'}
              </span>
            </div>

            {/* Question Text Display */}
            {q.questionText && (
              <div className="mb-4 bg-blue-50/50 p-3 rounded-lg border border-blue-100">
                <p className="text-xs uppercase tracking-wide text-blue-800 font-bold mb-1">Question</p>
                <p className="text-gray-800 text-sm font-medium">{q.questionText}</p>
              </div>
            )}
            
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <div className="flex justify-between items-center mb-1">
                  <p className="text-xs uppercase tracking-wide text-gray-400 font-bold">Student Answer (Verbatim)</p>
                  {q.spellingErrors && q.spellingErrors.length > 0 && (
                     <span className="text-[10px] font-bold bg-red-100 text-red-600 px-2 py-0.5 rounded-full">
                       {q.spellingErrors.length} Spelling Error{q.spellingErrors.length > 1 ? 's' : ''}
                     </span>
                  )}
                </div>
                
                <div className="bg-gray-50 p-3 rounded-md text-gray-700 text-sm italic border-l-4 border-gray-300 relative group h-full">
                  "{q.studentAnswerSummary}"
                  {q.spellingErrors && q.spellingErrors.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-gray-200">
                      <p className="text-xs font-semibold text-red-500">Spelling detected: {q.spellingErrors.join(", ")}</p>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex flex-col gap-3">
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-400 font-bold mb-1">Student Feedback</p>
                  <div className="text-gray-600 text-sm leading-relaxed">
                    {q.critique}
                    <div className="mt-2 text-xs text-gray-400 bg-gray-50 inline-block px-2 py-1 rounded">
                      Rubric Rule: {q.rubricReference}
                    </div>
                  </div>
                </div>

                {/* Evaluation Logic Section (Verification) */}
                {showLogic && q.evaluationLogic && (
                  <div className="mt-2 animate-fade-in">
                    <p className="text-xs uppercase tracking-wide text-purple-600 font-bold mb-1 flex items-center gap-1">
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
                      Teacher Verification Logic
                    </p>
                    <div className="bg-purple-50 p-3 rounded-lg border border-purple-100 text-xs text-purple-900 font-mono leading-relaxed whitespace-pre-line">
                      {q.evaluationLogic}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Actions */}
      <div className="flex justify-between items-center pt-8 border-t border-gray-200">
        <button 
          onClick={onReset}
          className="text-gray-600 hover:text-gray-900 font-medium flex items-center gap-2"
        >
          ← Grade Another Batch
        </button>
        <div className="flex gap-2">
            <button 
              onClick={handleDownloadLogicLog}
              className="bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 px-4 py-2 rounded-lg shadow-sm font-medium transition flex items-center gap-2 text-sm"
              title="Download internal logic for verification"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
              Save Logic Log
            </button>
            <button 
              onClick={handleDownloadCurrentPDF}
              className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-2 rounded-lg shadow-sm font-medium transition flex items-center gap-2"
            >
              Download This PDF
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
            </button>
        </div>
      </div>
    </div>
  );
};

export default ResultsView;
